import { h } from 'preact'
import { useState } from 'preact/hooks'
import IconButton from 'components/IconButton'
import EndUserAvatar from 'components/EndUserAvatar'
import ChatChannelAvatar from 'components/ChatChannelAvatar'
import { ChatWindow } from '../../a-portal/pages/OrganizationChatPage/ChatWindow'
import { useChatChannel, useFasterChatPolling } from 'lib/chatHooks'
import { useMyOrganizations } from 'lib/membershipAppStateHooks'
import ThreadRepliesModal from 'components/ThreadRepliesModal'

export default function FloatingChatConversation({
  chat,
  onClose,
  myPublicProfile
}) {
  const [isMinimized, setIsMinimized] = useState(false)
  const [isThreadOpen, setIsThreadOpen] = useState(false)
  const [replyingTo, setReplyingTo] = useState(null)
  const [isEditing, setIsEditing] = useState(null)
  const [isEditingThread, setIsEditingThread] = useState(null)
  const [editedMessage, setEditedMessage] = useState("")
  const [editMessageUid, setEditMessageUid] = useState(null)
  
  useFasterChatPolling('FloatingChatConversation')

  // Get the chat channel data and all related functionality
  const {
    chatChannel,
    chatMessages,
    pendingChatMessages,
    createChatMessage,
    editChatMessage,
    replyToMessage,
    markAllChatMessagesAsRead,
    deleteChatMessage,
    undoDeleteChatMessage,
    starChatMessage,
    unstarChatMessage,
    deleteChatChannel,
    leaveChannel,
    createChatMessageReaction,
    removeChatMessageReaction,
    markChatMessageAsRead,
  } = useChatChannel(chat.uid, 'FloatingChatConversation')

  const { myOrganizations } = useMyOrganizations('FloatingChatConversation')

  // Find the organization for this chat
  const organization = myOrganizations?.find(org =>
    org.apikey === chat.organizationApikey
  ) || myOrganizations?.[0] // Fallback to first org if not found

  const isDM = chat.type === 'direct_chat' || chat.type === 'dm'
  const displayName = isDM
    ? (chat.name || chat.displayName || 'Unknown User')
    : (chat.name || chat.displayName || 'Group Chat')

  const toggleMinimized = () => {
    setIsMinimized(!isMinimized)
  }

  const openThreadRepliesModal = () => {
    setIsThreadOpen(true)
  }

  const closeThreadRepliesModal = () => {
    setIsThreadOpen(false)
    setReplyingTo(null)
  }

  if (!organization) {
    return (
      <div className={`FloatingChatConversation ${isMinimized ? 'minimized' : 'expanded'}`}>
        <div className="FloatingChatConversation-header">
          <div className="FloatingChatConversation-info" onClick={toggleMinimized}>
            <div className="FloatingChatConversation-name">Loading...</div>
          </div>
          <div className="FloatingChatConversation-controls">
            <IconButton
              type="expand"
              size="xs"
              href={`/chat/${chat.uid}`}
            />
            <IconButton
              type="close"
              size="xs"
              onClick={(e) => {
                e.stopPropagation()
                onClose()
              }}
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`FloatingChatConversation ${isMinimized ? 'minimized' : 'expanded'}`}>
      <div className="FloatingChatConversation-header">
        <div className="FloatingChatConversation-info" onClick={toggleMinimized}>
          <div className="FloatingChatConversation-avatar">
            {isDM ? (
              <EndUserAvatar
                endUser={{ displayName }}
                size="xs"
              />
            ) : (
              <ChatChannelAvatar
                chatChannel={chat}
                size="xs"
              />
            )}
          </div>
          <div className="FloatingChatConversation-name">{displayName}</div>
        </div>
        <div className="FloatingChatConversation-controls">
          <IconButton
            type="expand"
            size="xs"
            href={`/chat/${chat.uid}`}
            onClick={(e) => {
              e.stopPropagation()
            }}
          />
          <IconButton
            type="close"
            size="xs"
            onClick={(e) => {
              e.stopPropagation()
              onClose()
            }}
          />
        </div>
      </div>

      {!isMinimized && (
        <div className="FloatingChatConversation-content">
          {isThreadOpen ? (
            <ThreadRepliesModal
              close={closeThreadRepliesModal}
              chatChannelUid={chat.uid}
              organization={organization}
              replyingTo={replyingTo}
              setReplyingTo={setReplyingTo}
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              isEditingThread={isEditingThread}
              setIsEditingThread={setIsEditingThread}
              editedMessage={editedMessage}
              setEditedMessage={setEditedMessage}
              editMessageUid={editMessageUid}
              setEditMessageUid={setEditMessageUid}
            />
          ) : (
            <ChatWindow
              organization={organization}
              myPublicProfile={myPublicProfile}
              uid={chat.uid}
              chatMessages={chatMessages}
              pendingChatMessages={pendingChatMessages}
              markAllChatMessagesAsRead={markAllChatMessagesAsRead}
              createChatMessage={createChatMessage}
              editChatMessage={editChatMessage}
              replyToMessage={replyToMessage}
              deleteChatMessage={deleteChatMessage}
              undoDeleteChatMessage={undoDeleteChatMessage}
              starChatMessage={starChatMessage}
              unstarChatMessage={unstarChatMessage}
              chatChannelUid={chat.uid}
              chatChannel={chatChannel}
              isPersonalChatPage={false}
              synopsis={[chat]}
              addReactionToMessage={createChatMessageReaction}
              handleRemoveReaction={removeChatMessageReaction}
              deleteChatChannel={deleteChatChannel}
              leaveChannel={leaveChannel}
              markChatMessageAsRead={markChatMessageAsRead}
              chatChannelType={chat.type}
              isOneToOne={isDM}
              header={displayName}
              openThreadRepliesModal={openThreadRepliesModal}
              closeThreadRepliesModal={closeThreadRepliesModal}
              replyingTo={replyingTo}
              setReplyingTo={setReplyingTo}
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              isEditingThread={isEditingThread}
              setIsEditingThread={setIsEditingThread}
              editedMessage={editedMessage}
              setEditedMessage={setEditedMessage}
              editMessageUid={editMessageUid}
              setEditMessageUid={setEditMessageUid}
            />
          )}
        </div>
      )}
    </div>
  )
}
